import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { demoPassword } from '../data/demo'
import { ionosAuth } from '../lib/apiClient'
import { isDemoMode } from '../lib/backend'
import type { Role, UserStatus } from '../types'

export interface AuthUser {
  id: string
  name: string
  email: string
  role: Role
  status: UserStatus
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  demoMode: boolean
  login: (email: string, password: string) => Promise<string | null>
  bootstrapAdmin: (name: string, email: string, password: string, setupKey: string) => Promise<string | null>
  register: (name: string, email: string, password: string) => Promise<string | null>
  resetPassword: (email: string) => Promise<string | null>
  updatePassword: (password: string, token?: string) => Promise<string | null>
  refreshUser: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)
const sessionKey = 'academia-demo-session-v1'

const messageFrom = (cause: unknown) => cause instanceof Error ? cause.message : 'No fue posible completar la solicitud.'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    if (!isDemoMode) return null
    const saved = sessionStorage.getItem(sessionKey)
    return saved ? { status: 'active', ...JSON.parse(saved) } as AuthUser : null
  })
  const [loading, setLoading] = useState(!isDemoMode)

  useEffect(() => {
    if (isDemoMode) return
    let active = true
    void ionosAuth.session().then(({ user: sessionUser }) => { if (active) setUser(sessionUser) }).catch(() => { if (active) setUser(null) }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    demoMode: isDemoMode,
    bootstrapAdmin: async (name, email, password, setupKey) => {
      if (isDemoMode) return 'La instalación solo está disponible en producción.'
      try {
        const result = await ionosAuth.bootstrapAdmin(name, email, password, setupKey)
        setUser(result.user)
        return null
      } catch (cause) { return messageFrom(cause) }
    },
    login: async (email, password) => {
      if (isDemoMode) {
        const normalized = email.trim().toLowerCase()
        if (password !== demoPassword || !['admin@demo.local', 'participante@demo.local'].includes(normalized)) return 'Correo o contraseña de demostración incorrectos.'
        const next: AuthUser = normalized.startsWith('admin')
          ? { id: 'user-admin', name: 'Marcela Administradora (demo)', email: normalized, role: 'admin', status: 'active' }
          : { id: 'user-participant', name: 'Ana Participante (demo)', email: normalized, role: 'participant', status: 'active' }
        setUser(next)
        sessionStorage.setItem(sessionKey, JSON.stringify(next))
        return null
      }
      try {
        const result = await ionosAuth.login(email, password)
        setUser(result.user)
        return null
      } catch (cause) {
        return messageFrom(cause)
      }
    },
    register: async (name, email, password) => {
      if (isDemoMode) return 'El registro está desactivado en modo demo.'
      try {
        const result = await ionosAuth.register(name, email, password)
        setUser(result.user)
        return null
      } catch (cause) { return messageFrom(cause) }
    },
    resetPassword: async (email) => {
      if (isDemoMode) return 'En modo demo no se envían correos.'
      try { await ionosAuth.resetPassword(email); return null } catch (cause) { return messageFrom(cause) }
    },
    updatePassword: async (password, token) => {
      if (isDemoMode) return 'La actualización de contraseña no está disponible en modo demo.'
      try { await ionosAuth.updatePassword(password, token); return null } catch (cause) { return messageFrom(cause) }
    },
    refreshUser: async () => {
      if (isDemoMode) return
      try { setUser((await ionosAuth.session()).user) } catch { setUser(null) }
    },
    logout: async () => {
      if (isDemoMode) sessionStorage.removeItem(sessionKey)
      else { try { await ionosAuth.logout() } catch { /* La sesión local se cierra igualmente. */ } }
      setUser(null)
    },
  }), [loading, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider')
  return context
}
